import React, { useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'
import { MailCheck } from 'lucide-react'

/**
 * AuthModal Component
 * Modern authentication modal with brand styling
 * Handles login, signup, and password reset functionality
 */
const AuthModal = ({ isOpen, onClose, initialMode = 'login', initialMessage = '' }) => {
  const { signIn, signUp, resetPassword } = useAuth()
  const [mode, setMode] = useState(initialMode) // 'login', 'signup', 'reset', 'check-email'
  const [email, setEmail] = useState('')
  const [confirmationEmail, setConfirmationEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return

    setMode(initialMode)
    setMessage(initialMessage)
    setError('')
  }, [initialMode, initialMessage, isOpen])

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      if (mode === 'signup') {
        // Validate password confirmation
        if (password !== confirmPassword) {
          setError('Passwords do not match')
          setLoading(false)
          return
        }

        const { data, error } = await signUp(email, password)
        if (error) {
          setError(error.message)
        } else {
          // Check if user needs email confirmation or is immediately available
          if (data?.user && !data.user.email_confirmed_at && data.user.confirmation_sent_at) {
            setConfirmationEmail(email)
            setMode('check-email')
            setPassword('')
            setConfirmPassword('')
          } else {
            setMessage('Account created successfully! You can now sign in.')
            setMode('login')
            setPassword('')
            setConfirmPassword('')
          }
        }
      } else if (mode === 'login') {
        const { error } = await signIn(email, password)
        if (error) {
          setError(error.message)
        } else {
          onClose()
        }
      } else if (mode === 'reset') {
        const { error } = await resetPassword(email)
        if (error) {
          setError(error.message)
        } else {
          setMessage('If an account exists for that email, we sent password reset instructions.')
        }
      }
    } catch (err) {
      setError('An unexpected error occurred')
    }

    setLoading(false)
  }

  // Reset form when switching modes
  const switchMode = (newMode) => {
    setMode(newMode)
    setError('')
    setMessage('')
    setPassword('')
    setConfirmPassword('')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md md:max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl md:text-3xl">
            {mode === 'login' && 'Welcome Back'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'reset' && 'Reset Password'}
            {mode === 'check-email' && 'Check Your Email'}
          </CardTitle>
          <CardDescription className="md:text-lg">
            {mode === 'login' && 'Sign in to search millions of recipes'}
            {mode === 'signup' && 'Join us to search millions of recipes'}
            {mode === 'reset' && 'Enter your email to reset your password'}
            {mode === 'check-email' && 'Confirm your account to start exploring recipes'}
          </CardDescription>
        </CardHeader>

        {mode === 'check-email' ? (
          <>
            <CardContent className="space-y-4 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-brand md:h-16 md:w-16">
                <MailCheck className="h-7 w-7 md:h-8 md:w-8" aria-hidden="true" strokeWidth={2.25} />
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-700 md:text-base">
                  We sent a confirmation link to{' '}
                  <span className="font-semibold text-gray-900">{confirmationEmail || email}</span>.
                </p>
                <p className="text-sm text-gray-600 md:text-base">
                  Open that email and confirm your account. After confirmation, return here and sign in.
                </p>
              </div>
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800 md:text-base">
                The link may take a minute to arrive. Check your spam folder if you do not see it.
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-3">
              <Button
                type="button"
                className="w-full md:h-11 md:text-base"
                onClick={() => switchMode('login')}
              >
                Back to sign in
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="w-full md:h-11 md:text-base"
              >
                Close
              </Button>
            </CardFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
            {/* Email Input */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 md:text-lg">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="md:h-12 md:text-lg md:placeholder:text-lg"
                required
              />
            </div>

            {/* Password Input - Hidden for reset mode */}
            {mode !== 'reset' && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 md:text-lg">
                  Password
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="md:h-12 md:text-lg md:placeholder:text-lg"
                  required
                  minLength={6}
                />
              </div>
            )}

            {/* Confirm Password - Only for signup */}
            {mode === 'signup' && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 md:text-lg">
                  Confirm Password
                </label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="md:h-12 md:text-lg md:placeholder:text-lg"
                  required
                  minLength={6}
                />
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="rounded-md bg-red-50 p-2 text-center text-sm text-red-500 md:text-base">
                {error}
              </div>
            )}

            {/* Success Message */}
            {message && (
              <div className="rounded-md bg-green-50 p-2 text-center text-sm text-green-600 md:text-base">
                {message}
              </div>
            )}
            </CardContent>

            <CardFooter className="flex flex-col space-y-3">
            {/* Submit Button */}
            <Button type="submit" className="w-full md:h-11 md:text-base" disabled={loading}>
              {loading ? 'Please wait...' : (
                mode === 'login' ? 'Sign In' :
                mode === 'signup' ? 'Create Account' :
                'Send Reset Email'
              )}
            </Button>

            {/* Mode Switch Buttons */}
            <div className="space-y-2 text-center">
              {mode === 'login' && (
                <>
                  <p className="text-sm text-gray-600 md:text-lg">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('signup')}
                      className="font-medium text-brand hover:underline"
                    >
                      Sign up
                    </button>
                  </p>
                  <p className="text-sm text-gray-600 md:text-lg">
                    <button
                      type="button"
                      onClick={() => switchMode('reset')}
                      className="font-medium text-brand hover:underline"
                    >
                      Forgot password?
                    </button>
                  </p>
                </>
              )}

              {mode === 'signup' && (
                <p className="text-sm text-gray-600 md:text-lg">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="font-medium text-brand hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              )}

              {mode === 'reset' && (
                <p className="text-sm text-gray-600 md:text-lg">
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="font-medium text-brand hover:underline"
                  >
                    Back to sign in
                  </button>
                </p>
              )}
            </div>

            {/* Close Button */}
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="w-full md:h-11 md:text-base"
            >
              Close
            </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  )
}

export default AuthModal
